/**
 * useNavOrder — congela el orden de las fichas mientras se recorren.
 *
 * PROBLEMA QUE RESUELVE
 * ---------------------------------------------------------------------------
 * El paginador de las fichas recibía el array VIVO del listado
 * (`filteredServers`, `filtered`, …). Si al guardar el listado se recarga y
 * cambia el orden (en servidores y cabinas el backend ordenaba solo por
 * `created_at desc`, sin desempate: al actualizar, la fila se va al final de
 * la tabla y reaparecía detrás de todas), el contador ‹ 3 / 12 › saltaba de
 * sitio y el «siguiente» ya no era el siguiente.
 *
 * SOLUCIÓN
 * ---------------------------------------------------------------------------
 * Al abrir la ficha se toma una FOTO de los ids en el orden en que se ven.
 * Mientras la ficha siga abierta, el paginador recorre esa foto; los datos de
 * cada registro se resuelven contra las filas actuales (así un cambio de
 * nombre se refleja), pero el ORDEN no cambia. Al cerrar la ficha se tira la
 * foto: la próxima vez se vuelve a congelar con el orden del listado (filtros
 * y ordenación de columnas incluidos).
 *
 * Si el registro abierto no está en la foto (alta, o el filtro lo ha sacado),
 * se refresca la foto para que el contador no desaparezca.
 */
import { useEffect, useRef, useState } from 'react';

const keyOf = (row) => {
  const id = row?.id;
  return id === undefined || id === null ? null : String(id);
};

export default function useNavOrder(rows, selectedRow, isOpen) {
  const snapshotRef = useRef([]);
  const [order, setOrder] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      if (snapshotRef.current.length) {
        snapshotRef.current = [];
        setOrder([]);
      }
      return;
    }

    const selectedKey = keyOf(selectedRow);
    const current = snapshotRef.current;
    const needsSnapshot = current.length === 0
      || (selectedKey && !current.includes(selectedKey));

    if (!needsSnapshot) return;

    const next = (rows || []).map(keyOf).filter(Boolean);
    if (!next.length) return;
    snapshotRef.current = next;
    setOrder(next);
  }, [isOpen, rows, selectedRow]);

  if (!isOpen || !order.length) return rows || [];

  const byId = new Map((rows || []).map((row) => [keyOf(row), row]));
  const frozen = order.map((id) => byId.get(id)).filter(Boolean);
  // Una fila puede desaparecer (borrada, filtrada): el resto conserva su sitio.
  return frozen.length ? frozen : (rows || []);
}
